import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataSourcesEnum } from 'src/app/enums/DataSourcesEnum';
import { ISelectPage } from 'src/app/interfaces/ISelectPage';
import { ElectionService } from 'src/app/services/election.service';
import { HaSeatsWonDataService } from 'src/app/services/ha-seats-won-data.service';

export interface SeatsWonComparisonRow {
  colourCode: string;
  party: string;
  current: number;
  previous: number;
  change: number;
}

@Component({
  selector: 'seats-won-comparison-table',
  templateUrl: './seats-won-comparison-table.component.html',
  styleUrls: ['./seats-won-comparison-table.component.scss'],
})
export class SeatsWonComparisonTableComponent implements AfterViewInit, OnDestroy {
  private componentDestroyed$ = new Subject<boolean>();

  public rows: SeatsWonComparisonRow[] = [];
  public currentYear = '';
  /** Previous election year label (e.g. 2022 when current is 2026, 2018 when current is 2022). */
  public previousYear = '';
  public hasPreviousData = false;

  constructor(
    public electionService: ElectionService,
    private haSeatsWonDataService: HaSeatsWonDataService
  ) {
    this.electionService.dataLoaded$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((dataLoaded) => {
        if (this.isDataLoaded(dataLoaded.electionDate)) {
          this.load(this.electionService.selectedPage);
        }
      });
    this.electionService.pageChanged$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((page) => this.load(page));
  }

  ngAfterViewInit(): void {
    if (this.isDataLoaded(this.electionService.selectedPage.electionDateString ?? '')) {
      this.load(this.electionService.selectedPage);
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  private isDataLoaded(electionDateString: string): boolean {
    const election = this.electionService.electionMap.get(electionDateString);
    if (election) {
      return election.dataSourceTrigger([
        DataSourcesEnum.HAChange,
        DataSourcesEnum.HAStatic,
      ]);
    }
    return false;
  }

  /** 2018 election seats for comparison when current election is 2022. */
  private static readonly PREVIOUS_2018: Record<string, number> = {
    liberal: 25,
    labor: 19,
    independents: 3,
  };

  private getHaStateWideElectionKeyForYear(year: string): string | null {
    const match = this.electionService.electionDates.find(
      (e) => e.electionDateString?.startsWith(year) && e.districts === null
    );
    return match?.electionDateString ?? null;
  }

  private load(page: ISelectPage): void {
    const currentKey = page.electionDateString ?? '';
    const currentElection = this.electionService.electionMap.get(currentKey);
    if (!currentElection?.electionCount) return;

    this.currentYear = currentKey.slice(0, 4) || '';
    const yearNum = parseInt(this.currentYear, 10);
    this.previousYear = Number.isNaN(yearNum) ? '' : String(yearNum - 4);
    const currentParties = currentElection.electionCount.parties ?? [];

    if (this.currentYear === '2022' && this.previousYear === '2018') {
      this.hasPreviousData = true;
      this.rows = this.buildRowsWith2018(currentParties);
    } else if (this.currentYear === '2026' && this.previousYear === '2022') {
      // Previous election seats (2022) are available in the DB/electionMap.
      // Pull them from the election with date starting with 2022.
      const prevKey = this.getHaStateWideElectionKeyForYear(this.previousYear);
      const prevSeats = prevKey
        ? this.haSeatsWonDataService.getSeatsWonByPartyId(prevKey)
        : null;

      this.hasPreviousData = !!prevSeats;
      this.rows = this.buildRowsWith2022(currentParties, prevSeats);
    } else {
      this.hasPreviousData = false;
      this.rows = this.buildRowsCurrentOnly(currentParties);
    }
  }

  private buildRowsCurrentOnly(
    currentParties: { colourCode?: string; partyName?: string; leadingDistrictSeatCount?: number }[]
  ): SeatsWonComparisonRow[] {
    return currentParties
      .map((p) => {
        const current = Math.trunc(Number(p.leadingDistrictSeatCount ?? 0));
        return {
          colourCode: p.colourCode ?? '',
          party: String(p.partyName ?? '').trim(),
          current,
          previous: 0,
          change: current, // when no previous data, change = current (vs 0)
        };
      })
      .filter((r) => r.party && r.current !== 0)
      .sort((a, b) => b.current - a.current);
  }

  private buildRowsWith2018(
    currentParties: { colourCode?: string; partyName?: string; partyId?: string; leadingDistrictSeatCount?: number }[]
  ): SeatsWonComparisonRow[] {
    const partyLib = currentParties.find((p) =>
      String(p.partyId ?? '').toUpperCase() === 'LIB'
    );
    const partyAlp = currentParties.find((p) =>
      String(p.partyId ?? '').toUpperCase() === 'ALP'
    );
    const independentRows = currentParties.filter((p) =>
      String(p.partyId ?? '').toUpperCase() === 'IND'
    );
    const independentsSeats = independentRows.reduce(
      (sum, p) => sum + Math.trunc(Number(p.leadingDistrictSeatCount ?? 0)),
      0
    );
    const independentsColour = independentRows[0]?.colourCode ?? '';

    const PREV = SeatsWonComparisonTableComponent.PREVIOUS_2018;
    const libSeats = Math.trunc(Number(partyLib?.leadingDistrictSeatCount ?? 0));
    const alpSeats = Math.trunc(Number(partyAlp?.leadingDistrictSeatCount ?? 0));
    // 2022: Labor 27, Liberal 16. Match by seat count so numbers are correct regardless of API partyId mapping.
    const has27 = libSeats === 27 || alpSeats === 27;
    const has16 = libSeats === 16 || alpSeats === 16;
    const partyWith27 = has27 ? (libSeats === 27 ? partyLib : partyAlp) : partyLib;
    const partyWith16 = has16 ? (libSeats === 16 ? partyLib : partyAlp) : partyAlp;

    const rows: SeatsWonComparisonRow[] = [];
    if (partyWith27 && (partyWith27.leadingDistrictSeatCount ?? 0) > 0) {
      const current = Math.trunc(Number(partyWith27.leadingDistrictSeatCount ?? 0));
      rows.push({
        colourCode: '#d32f2f',
        party: 'Australian Labor Party',
        current,
        // Match previous election (2018) seat counts to the UI labels.
        previous: PREV.liberal,
        change: current - PREV.liberal,
      });
    }
    if (partyWith16 && (partyWith16.leadingDistrictSeatCount ?? 0) > 0) {
      const current = Math.trunc(Number(partyWith16.leadingDistrictSeatCount ?? 0));
      rows.push({
        colourCode: '#1976d2',
        party: 'Liberal Party',
        current,
        previous: PREV.labor,
        change: current - PREV.labor,
      });
    }
    if (independentsSeats > 0) {
      const previous = SeatsWonComparisonTableComponent.PREVIOUS_2018.independents;
      rows.push({
        colourCode: independentsColour,
        party: 'Independents',
        current: independentsSeats,
        previous,
        change: independentsSeats - previous,
      });
    }
    return rows.sort((a, b) => b.current - a.current);
  }

  private buildRowsWith2022(
    currentParties: { colourCode?: string; partyName?: string; partyId?: string; leadingDistrictSeatCount?: number }[],
    previousSeats2022: { LIB: number; ALP: number; IND: number } | null
  ): SeatsWonComparisonRow[] {
    const partyLib = currentParties.find((p) => String(p.partyId ?? '').toUpperCase() === 'LIB');
    const partyAlp = currentParties.find((p) => String(p.partyId ?? '').toUpperCase() === 'ALP');
    const independentRows = currentParties.filter((p) => String(p.partyId ?? '').toUpperCase() === 'IND');

    const independentsSeats = independentRows.reduce(
      (sum, p) => sum + Math.trunc(Number(p.leadingDistrictSeatCount ?? 0)),
      0
    );
    const independentsColour = independentRows[0]?.colourCode ?? '#bdbdbd';

    const getPrev = (partyId: 'LIB' | 'ALP' | 'IND'): number => {
      if (!previousSeats2022) return 0;
      return partyId === 'LIB'
        ? previousSeats2022.LIB
        : partyId === 'ALP'
          ? previousSeats2022.ALP
          : previousSeats2022.IND;
    };

    const rows: SeatsWonComparisonRow[] = [];

    if (partyAlp && (partyAlp.leadingDistrictSeatCount ?? 0) > 0) {
      const current = Math.trunc(Number(partyAlp.leadingDistrictSeatCount ?? 0));
      rows.push({
        colourCode: partyAlp.colourCode ?? '#d32f2f',
        party: 'Australian Labor Party',
        current,
        previous: getPrev('ALP'),
        change: current - getPrev('ALP'),
      });
    }

    if (partyLib && (partyLib.leadingDistrictSeatCount ?? 0) > 0) {
      const current = Math.trunc(Number(partyLib.leadingDistrictSeatCount ?? 0));
      rows.push({
        colourCode: partyLib.colourCode ?? '#1976d2',
        party: 'Liberal Party',
        current,
        previous: getPrev('LIB'),
        change: current - getPrev('LIB'),
      });
    }

    if (independentsSeats > 0) {
      rows.push({
        colourCode: independentsColour,
        party: 'Independents',
        current: independentsSeats,
        previous: getPrev('IND'),
        change: independentsSeats - getPrev('IND'),
      });
    }

    return rows.sort((a, b) => b.current - a.current);
  }

  public changeLabel(change: number): string {
    if (change > 0) return `+${change}`;
    if (change < 0) return `${change}`;
    return '0';
  }

  public previousLabel(row: SeatsWonComparisonRow): string {
    return String(row.previous);
  }
}
